function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { ClearButton, FilterButton } from '../buttons';
import { DatePicker } from '../date';
import { chunkArray } from '../arrayUtils/arrayUtils';
import MultiChoiceFilter, { MULTI_CHOICE_FILTER_CONTROL_MIN_WIDTH, MULTI_CHOICE_FILTER_INPUT_OFFSET } from './MultiChoiceFilter';
import { useTranslation } from 'react-i18next';
const filterControlAlignmentStyle = {
  paddingLeft: MULTI_CHOICE_FILTER_INPUT_OFFSET
};
const dateFilterControlStyle = _objectSpread(_objectSpread({}, filterControlAlignmentStyle), {}, {
  minWidth: MULTI_CHOICE_FILTER_CONTROL_MIN_WIDTH
});

// Checks if a table header should get a filter input.
// Example: if a header says "filterableByText: true", we show a text box for it.
// If it has none of these filter flags, it is like a toy with no button: there is nothing to press, so we skip it.
export const isFilterableHeader = header => header.filterableByText || header.filterableBySelect || header.filterableByDate;
export const hasFilterableHeaders = headers => headers.some(isFilterableHeader);

// Picks the name we use to store this filter's value.
// Example: "Project Name" might use filterBy: "project_name", so the saved filters look like { project_name: "Battery" }.
// If filterBy is missing, we use header.key, and if that is missing too, we make a name from the label and position.
const defaultGetFilterKey = (header, index) => header.filterBy || header.key || "".concat(header.text || header.name, "-").concat(index);
export default function TableColumnFilter(_ref) {
  let {
    headers,
    filters = {},
    getFilterKey = defaultGetFilterKey,
    onApplyFilters,
    isExpanded
  } = _ref;
  const [filterValues, setFilterValues] = useState(filters);
  const {
    t
  } = useTranslation();
  const filterableHeaders = useMemo(() => headers.map((header, headerIndex) => ({
    header,
    headerIndex
  })).filter(_ref2 => {
    let {
      header
    } = _ref2;
    return isFilterableHeader(header);
  }), [headers]);
  useEffect(() => {
    setFilterValues(filters);
  }, [filters]);
  if (filterableHeaders.length === 0) {
    return null;
  }
  if (!isExpanded) {
    return null;
  }
  const filterColumns = chunkArray(filterableHeaders, 2);
  const setFilterValue = (filterKey, value) => {
    setFilterValues(currentFilterValues => _objectSpread(_objectSpread({}, currentFilterValues), {}, {
      [filterKey]: value || ''
    }));
  };
  const activeFilterValues = values => filterableHeaders.reduce((activeFilters, _ref3) => {
    let {
      header,
      headerIndex
    } = _ref3;
    const filterKey = getFilterKey(header, headerIndex);
    const value = values[filterKey];
    if (value !== null && value !== undefined && value !== '') {
      activeFilters[filterKey] = value;
    }
    return activeFilters;
  }, {});
  const applyFilters = () => {
    onApplyFilters(activeFilterValues(filterValues));
  };
  const clearFilters = () => {
    setFilterValues({});
    onApplyFilters({});
  };
  const renderFilterControl = (header, filterKey) => {
    const value = filterValues[filterKey] || '';
    if (header.filterableBySelect) {
      // Dropdown supports strings and { key, value, text }; year filters can be numbers, so normalize primitives first.
      const options = (header.filterOptions || []).map(option => typeof option === 'object' && option !== null ? option : {
        key: option,
        value: option,
        text: option
      });
      return /*#__PURE__*/React.createElement(MultiChoiceFilter, {
        dataTestId: "table-column-filter-".concat(filterKey),
        dropdownStyle: {
          width: '100%',
          minWidth: 0
        },
        onChange: nextValue => setFilterValue(filterKey, nextValue),
        options: options,
        type: "select",
        value: value
      });
    }
    if (header.filterableByDate) {
      return /*#__PURE__*/React.createElement("div", {
        style: dateFilterControlStyle
      }, /*#__PURE__*/React.createElement(DatePicker, {
        selected: value,
        onChange: date => setFilterValue(filterKey, date)
      }));
    }
    return /*#__PURE__*/React.createElement(MultiChoiceFilter, {
      type: "text",
      value: value,
      onChange: nextValue => setFilterValue(filterKey, nextValue)
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "TableColumnFilter"
  }, /*#__PURE__*/React.createElement("div", {
    className: "border rounded p-3 mt-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-2",
    style: {
      overflowX: 'auto'
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex gap-3",
    style: {
      width: 'max-content'
    }
  }, filterColumns.map((filterColumn, filterColumnIndex) => /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-column gap-2",
    key: "filter-column-".concat(filterColumnIndex),
    style: {
      width: '280px'
    }
  }, filterColumn.map(_ref4 => {
    let {
      header,
      headerIndex
    } = _ref4;
    const filterKey = getFilterKey(header, headerIndex);
    return /*#__PURE__*/React.createElement(Form.Group, {
      className: "mb-2",
      key: filterKey
    }, /*#__PURE__*/React.createElement(Form.Label, {
      className: "font-weight-bold",
      style: filterControlAlignmentStyle
    }, t(header.text || header.name)), renderFilterControl(header, filterKey));
  }))))), /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center gap-2 mt-3"
  }, /*#__PURE__*/React.createElement(FilterButton, {
    onClick: applyFilters
  }), /*#__PURE__*/React.createElement(ClearButton, {
    onClick: clearFilters
  }))));
}